import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { HiUpload, HiTrash, HiMusicNote, HiPlus, HiX, HiPencil, HiCake } from 'react-icons/hi';
import { FaSpotify, FaYoutube } from 'react-icons/fa';
import { supabase } from '../supabaseClient';

const AdminPanel = () => {
  const [tracks, setTracks] = useState([]);
  const [birthdays, setBirthdays] = useState([]); // Add birthdays state
  const [activeTab, setActiveTab] = useState('music'); // Add active tab state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Form fields for new track
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [album, setAlbum] = useState('');
  const [duration, setDuration] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [roles, setRoles] = useState([{ role: '', name: '' }]);
  
  // Form fields for new birthday
  const [birthdayName, setBirthdayName] = useState('');
  const [birthdayDate, setBirthdayDate] = useState('');
  const [birthdayMessage, setBirthdayMessage] = useState('');
  const [birthdayAvatar, setBirthdayAvatar] = useState('');
  const [birthdayFavoriteColor, setBirthdayFavoriteColor] = useState('');
  const [birthdaySnapchat, setBirthdaySnapchat] = useState('');
  
  // Edit track state
  const [editingTrackId, setEditingTrackId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editArtist, setEditArtist] = useState('');
  const [editAlbum, setEditAlbum] = useState('');
  const [editDuration, setEditDuration] = useState('');
  const [editSpotifyUrl, setEditSpotifyUrl] = useState('');
  const [editYoutubeUrl, setEditYoutubeUrl] = useState('');
  const [editRoles, setEditRoles] = useState([]);
  
  // Edit birthday state
  const [editingBirthdayId, setEditingBirthdayId] = useState(null);
  const [editBirthdayName, setEditBirthdayName] = useState('');
  const [editBirthdayDate, setEditBirthdayDate] = useState('');
  const [editBirthdayMessage, setEditBirthdayMessage] = useState('');
  const [editBirthdayAvatar, setEditBirthdayAvatar] = useState('');
  const [editBirthdayFavoriteColor, setEditBirthdayFavoriteColor] = useState('');
  const [editBirthdaySnapchat, setEditBirthdaySnapchat] = useState('');
  
  // File references
  const audioFileRef = useRef(null);
  const coverImageRef = useRef(null);

  // Check if user is already logged in
  useEffect(() => {
    const checkUser = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Auth error:', error);
      } else if (user) {
        console.log('Authenticated user:', user);
        setUser(user);
        setIsLoggedIn(true);
        fetchTracks();
        fetchBirthdays(); // Fetch birthdays when logged in
      }
    };
    
    checkUser();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state change:', _event, session);
      if (session?.user) {
        console.log('Session user:', session.user);
        setUser(session.user);
        setIsLoggedIn(true);
        fetchTracks();
        fetchBirthdays(); // Fetch birthdays when auth state changes
      } else {
        setUser(null);
        setIsLoggedIn(false);
      }
    });
    
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        setError(error.message);
      } else {
        setUser(data.user);
        setIsLoggedIn(true);
        fetchTracks();
        fetchBirthdays(); // Fetch birthdays after login
      }
    } catch (err) {
      setError('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
    } else {
      setUser(null);
      setIsLoggedIn(false);
      setEmail('');
      setPassword('');
    }
  };

  const fetchTracks = async () => {
    try {
      const { data, error } = await supabase
        .from('music_tracks')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching tracks:', error);
      } else {
        setTracks(data || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Fetch birthdays from Supabase
  const fetchBirthdays = async () => {
    try {
      const { data, error } = await supabase
        .from('birthdays')
        .select('*')
        .order('name');
      
      if (error) {
        console.error('Error fetching birthdays:', error);
      } else {
        setBirthdays(data || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Role management functions
  const addRole = () => {
    setRoles([...roles, { role: '', name: '' }]);
  };

  const removeRole = (index) => {
    const newRoles = [...roles];
    newRoles.splice(index, 1);
    setRoles(newRoles);
  };

  const updateRole = (index, field, value) => {
    const newRoles = [...roles];
    newRoles[index][field] = value;
    setRoles(newRoles);
  };

  // File upload function
  const uploadFile = async (file, bucketName, filePath) => {
    try {
      // Sanitize the file name to remove special characters that might cause issues
      const sanitizedFileName = filePath.replace(/[^a-zA-Z0-9\-_./]/g, '_');
      
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(sanitizedFileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(sanitizedFileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('You must be logged in to upload tracks');
      return;
    }
    
    console.log('Uploading as user:', user);
    
    // Filter out empty roles
    const filteredRoles = roles.filter(role => role.role.trim() !== '' || role.name.trim() !== '');
    
    try {
      setUploading(true);
      setUploadProgress(0);
      setError('');
      
      // Get files from refs
      const audioFile = audioFileRef.current?.files[0];
      const coverImage = coverImageRef.current?.files[0];
      
      // Upload files if provided
      let fileUrlToUse = fileUrl;
      let coverUrlToUse = coverUrl;
      
      if (audioFile) {
        const audioPath = `tracks/${Date.now()}_${audioFile.name}`;
        fileUrlToUse = await uploadFile(audioFile, 'music', audioPath);
      }
      
      if (coverImage) {
        const coverPath = `covers/${Date.now()}_${coverImage.name}`;
        coverUrlToUse = await uploadFile(coverImage, 'music', coverPath);
      }
      
      // Validate required fields
      if (!title) {
        throw new Error('Title is required');
      }
      
      if (!fileUrlToUse) {
        throw new Error('Audio file is required');
      }
      
      // Insert track data
      const { data, error } = await supabase
        .from('music_tracks')
        .insert([
          {
            title,
            artist: artist || null,
            album: album || null,
            duration: duration || null,
            file_url: fileUrlToUse,
            cover_url: coverUrlToUse || null,
            spotify_url: spotifyUrl || null,
            youtube_url: youtubeUrl || null,
            roles: filteredRoles
          }
        ])
        .select();
      
      if (error) {
        console.error('Database insert error:', error);
        throw new Error(`Database error: ${error.message}`);
      }
      
      console.log('Track inserted successfully:', data);
      
      // Reset form
      setTitle('');
      setArtist('');
      setAlbum('');
      setDuration('');
      setFileUrl('');
      setCoverUrl('');
      setSpotifyUrl('');
      setYoutubeUrl('');
      setRoles([{ role: '', name: '' }]);
      
      // Reset file inputs
      if (audioFileRef.current) audioFileRef.current.value = '';
      if (coverImageRef.current) coverImageRef.current.value = '';
      
      // Refresh tracks
      fetchTracks();
    } catch (err) {
      console.error('Upload error:', err);
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle birthday creation
  const handleCreateBirthday = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      
      // Validate required fields
      if (!birthdayName || !birthdayDate) {
        throw new Error('Name and birthday are required');
      }
      
      // Insert birthday data
      const { data, error } = await supabase
        .from('birthdays')
        .insert([
          {
            name: birthdayName,
            birthday: birthdayDate,
            message: birthdayMessage || null,
            avatar: birthdayAvatar || null,
            favorite_color: birthdayFavoriteColor || null,
            snapchat: birthdaySnapchat || null
          }
        ])
        .select();
      
      if (error) {
        console.error('Database insert error:', error);
        throw new Error(`Database error: ${error.message}`);
      }
      
      console.log('Birthday inserted successfully:', data);
      
      // Reset form
      setBirthdayName('');
      setBirthdayDate('');
      setBirthdayMessage('');
      setBirthdayAvatar('');
      setBirthdayFavoriteColor('');
      setBirthdaySnapchat('');
      
      // Refresh birthdays
      fetchBirthdays();
    } catch (err) {
      console.error('Create birthday error:', err);
      setError(`Create birthday failed: ${err.message}`);
    }
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm('Are you sure you want to delete this track? This will also delete the associated files.')) {
      return;
    }
    
    try {
      // First, get the track to retrieve file URLs
      const { data: trackData, error: fetchError } = await supabase
        .from('music_tracks')
        .select('file_url, cover_url')
        .eq('id', trackId)
        .single();
      
      if (fetchError) {
        console.error('Error fetching track data:', fetchError);
        throw fetchError;
      }
      
      // Delete files from storage if they exist
      if (trackData.file_url) {
        // Extract the file path from the Supabase storage URL
        // Supabase URLs have the format: .../storage/v1/object/public/{bucket}/{path}
        const fileUrlParts = trackData.file_url.split('/');
        const fileIndex = fileUrlParts.indexOf('public');
        if (fileIndex !== -1 && fileIndex + 2 < fileUrlParts.length) {
          const filePath = fileUrlParts.slice(fileIndex + 2).join('/');
          
          const { error: audioDeleteError } = await supabase
            .storage
            .from('music')
            .remove([filePath]);
            
          if (audioDeleteError) {
            console.error('Error deleting audio file:', audioDeleteError);
          }
        }
      }
      
      if (trackData.cover_url) {
        // Extract the file path from the Supabase storage URL
        const coverUrlParts = trackData.cover_url.split('/');
        const coverIndex = coverUrlParts.indexOf('public');
        if (coverIndex !== -1 && coverIndex + 2 < coverUrlParts.length) {
          const coverPath = coverUrlParts.slice(coverIndex + 2).join('/');
          
          const { error: coverDeleteError } = await supabase
            .storage
            .from('music')
            .remove([coverPath]);
            
          if (coverDeleteError) {
            console.error('Error deleting cover image:', coverDeleteError);
          }
        }
      }
      
      // Delete the track record from the database
      const { error: deleteError } = await supabase
        .from('music_tracks')
        .delete()
        .eq('id', trackId);
      
      if (deleteError) {
        console.error('Delete error:', deleteError);
        throw deleteError;
      }
      
      // Refresh tracks
      fetchTracks();
    } catch (err) {
      console.error('Delete error:', err);
      setError(`Delete failed: ${err.message}`);
    }
  };

  // Handle birthday deletion
  const handleDeleteBirthday = async (birthdayId) => {
    if (!window.confirm('Are you sure you want to delete this birthday?')) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('birthdays')
        .delete()
        .eq('id', birthdayId);
      
      if (error) {
        console.error('Delete error:', error);
        throw error;
      }
      
      // Refresh birthdays
      fetchBirthdays();
    } catch (err) {
      console.error('Delete birthday error:', err);
      setError(`Delete birthday failed: ${err.message}`);
    }
  };

  const startEditing = (track) => {
    setEditingTrackId(track.id);
    setEditTitle(track.title);
    setEditArtist(track.artist || '');
    setEditAlbum(track.album || '');
    setEditDuration(track.duration || '');
    setEditSpotifyUrl(track.spotify_url || '');
    setEditYoutubeUrl(track.youtube_url || '');
    // Clone roles array to avoid direct mutation
    setEditRoles(track.roles ? [...track.roles] : [{ role: '', name: '' }]);
  };

  // Start editing a birthday
  const startEditingBirthday = (birthday) => {
    setEditingBirthdayId(birthday.id);
    setEditBirthdayName(birthday.name);
    setEditBirthdayDate(birthday.birthday);
    setEditBirthdayMessage(birthday.message || '');
    setEditBirthdayAvatar(birthday.avatar || '');
    setEditBirthdayFavoriteColor(birthday.favorite_color || '');
    setEditBirthdaySnapchat(birthday.snapchat || '');
  };

  const cancelEditing = () => {
    setEditingTrackId(null);
    setEditTitle('');
    setEditArtist('');
    setEditAlbum('');
    setEditDuration('');
    setEditSpotifyUrl('');
    setEditYoutubeUrl('');
    setEditRoles([{ role: '', name: '' }]);
  };

  // Cancel editing a birthday
  const cancelEditingBirthday = () => {
    setEditingBirthdayId(null);
    setEditBirthdayName('');
    setEditBirthdayDate('');
    setEditBirthdayMessage('');
    setEditBirthdayAvatar('');
    setEditBirthdayFavoriteColor('');
    setEditBirthdaySnapchat('');
  };

  const saveEditing = async () => {
    if (!editingTrackId) return;
    
    try {
      // Filter out empty roles
      const filteredRoles = editRoles.filter(role => role.role.trim() !== '' || role.name.trim() !== '');
      
      const { error } = await supabase
        .from('music_tracks')
        .update({
          title: editTitle,
          artist: editArtist || null,
          album: editAlbum || null,
          duration: editDuration || null,
          spotify_url: editSpotifyUrl || null,
          youtube_url: editYoutubeUrl || null,
          roles: filteredRoles
        })
        .eq('id', editingTrackId);
      
      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }
      
      // Refresh tracks
      fetchTracks();
      cancelEditing();
    } catch (err) {
      console.error('Update error:', err);
      setError(`Update failed: ${err.message}`);
    }
  };

  // Save edited birthday
  const saveEditingBirthday = async () => {
    if (!editingBirthdayId) return;
    
    try {
      const { error } = await supabase
        .from('birthdays')
        .update({
          name: editBirthdayName,
          birthday: editBirthdayDate,
          message: editBirthdayMessage || null,
          avatar: editBirthdayAvatar || null,
          favorite_color: editBirthdayFavoriteColor || null,
          snapchat: editBirthdaySnapchat || null
        })
        .eq('id', editingBirthdayId);
      
      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }
      
      // Refresh birthdays
      fetchBirthdays();
      cancelEditingBirthday();
    } catch (err) {
      console.error('Update birthday error:', err);
      setError(`Update birthday failed: ${err.message}`);
    }
  };

  const addEditRole = () => {
    setEditRoles([...editRoles, { role: '', name: '' }]);
  };

  const removeEditRole = (index) => {
    const newRoles = [...editRoles];
    newRoles.splice(index, 1);
    setEditRoles(newRoles);
  };

  const updateEditRole = (index, field, value) => {
    const newRoles = [...editRoles];
    newRoles[index][field] = value;
    setEditRoles(newRoles);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen py-20 flex items-center justify-center">
        <div className="glass-morphism-strong rounded-3xl p-8 w-full max-w-md">
          <h2 className="text-2xl font-space-grotesk font-bold mb-6 text-center">Admin Login</h2>
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                required
              />
            </div>
            <div className="mb-6">
              <label className="block text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary flex items-center justify-center"
            >
              {isLoading ? (
                <span className="loading-dots">Logging in</span>
              ) : (
                'Login'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-12">
          <h1 className="text-display-2 font-space-grotesk font-bold">
            <span className="gradient-text-green">Admin</span> Panel
          </h1>
          <div className="flex items-center space-x-4">
            <span className="text-gray-300">Welcome, {user?.email}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex mb-8 border-b border-white/10">
          <button
            className={`py-3 px-6 font-medium text-sm rounded-t-lg transition-colors ${
              activeTab === 'music'
                ? 'text-accent-green border-b-2 border-accent-green'
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('music')}
          >
            <HiMusicNote className="inline mr-2" />
            Music Management
          </button>
          <button
            className={`py-3 px-6 font-medium text-sm rounded-t-lg transition-colors ${
              activeTab === 'birthdays'
                ? 'text-accent-green border-b-2 border-accent-green'
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('birthdays')}
          >
            <HiCake className="inline mr-2" />
            Birthday Management
          </button>
        </div>

        {/* Music Management Tab */}
        {activeTab === 'music' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Upload Form */}
            <div className="glass-morphism-strong rounded-3xl p-8">
              <h2 className="text-2xl font-space-grotesk font-bold mb-6 flex items-center">
                <HiUpload className="mr-2" />
                Upload New Track
              </h2>
              {error && (
                <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}
              {uploading && (
                <div className="mb-6">
                  <div className="w-full bg-gray-700 rounded-full h-2.5">
                    <div 
                      className="bg-accent-green h-2.5 rounded-full transition-all duration-300" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-center mt-2">Uploading... {Math.round(uploadProgress)}%</p>
                </div>
              )}
              <form onSubmit={handleUpload}>
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Artist</label>
                  <input
                    type="text"
                    value={artist}
                    onChange={(e) => setArtist(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                    placeholder="Artist name"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Album</label>
                    <input
                      type="text"
                      value={album}
                      onChange={(e) => setAlbum(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Duration</label>
                    <input
                      type="text"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      placeholder="e.g., 3:45"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                    />
                  </div>
                </div>
                
                {/* Audio File Upload */}
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Audio File</label>
                  <input
                    type="file"
                    ref={audioFileRef}
                    accept="audio/*"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                  />
                  <p className="text-sm text-gray-400 mt-1">Or provide a URL below</p>
                </div>
                
                {/* Audio File URL (fallback) */}
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Audio File URL (optional if uploading)</label>
                  <input
                    type="text"
                    value={fileUrl}
                    onChange={(e) => setFileUrl(e.target.value)}
                    placeholder="https://example.com/audio.mp3"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                  />
                </div>
                
                {/* Spotify URL */}
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Spotify Track URL</label>
                  <input
                    type="text"
                    value={spotifyUrl}
                    onChange={(e) => setSpotifyUrl(e.target.value)}
                    placeholder="https://open.spotify.com/track/..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                  />
                </div>
                
                {/* YouTube URL */}
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">YouTube Video URL</label>
                  <input
                    type="text"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                  />
                </div>
                
                {/* Cover Image Upload */}
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Cover Image/Video/GIF</label>
                  <input
                    type="file"
                    ref={coverImageRef}
                    accept="image/*,video/mp4,video/quicktime" // Allow images, MP4, and GIF (as video/quicktime)
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                  />
                  <p className="text-sm text-gray-400 mt-1">Supports images, MP4 videos, and GIFs</p>
                </div>
                
                {/* Cover Image URL (fallback) */}
                <div className="mb-6">
                  <label className="block text-gray-300 mb-2">Cover Image/Video/GIF URL (optional if uploading)</label>
                  <input
                    type="text"
                    value={coverUrl}
                    onChange={(e) => setCoverUrl(e.target.value)}
                    placeholder="https://example.com/cover.jpg or https://example.com/cover.mp4"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                  />
                </div>
                
                {/* Roles Section */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-gray-300">Roles & Contributors</label>
                    <button
                      type="button"
                      onClick={addRole}
                      className="text-accent-green hover:text-accent-green/80 flex items-center text-sm"
                    >
                      <HiPlus className="mr-1" /> Add Role
                    </button>
                  </div>
                  
                  {roles.map((role, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="Role (e.g., Producer, Vocalist)"
                        value={role.role}
                        onChange={(e) => updateRole(index, 'role', e.target.value)}
                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                      />
                      <input
                        type="text"
                        placeholder="Name"
                        value={role.name}
                        onChange={(e) => updateRole(index, 'name', e.target.value)}
                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                      />
                      {roles.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRole(index)}
                          className="text-red-400 hover:text-red-300 px-2"
                        >
                          <HiX />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                <button
                  type="submit"
                  disabled={uploading}
                  className="w-full btn-primary flex items-center justify-center"
                >
                  {uploading ? (
                    <span className="loading-dots">Uploading</span>
                  ) : (
                    <>
                      <HiUpload className="mr-2" />
                      Upload Track
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Stats */}
            <div className="glass-morphism-strong rounded-3xl p-8">
              <h2 className="text-2xl font-space-grotesk font-bold mb-6">Statistics</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/5 rounded-xl p-6">
                  <div className="text-3xl font-space-grotesk font-bold gradient-text-green mb-2">
                    {tracks.length}
                  </div>
                  <div className="text-gray-300">Total Tracks</div>
                </div>
                <div className="bg-white/5 rounded-xl p-6">
                  <div className="text-3xl font-space-grotesk font-bold gradient-text-green mb-2">
                    {[...new Set(tracks.map(track => track.artist).filter(Boolean))].length}
                  </div>
                  <div className="text-gray-300">Artists</div>
                </div>
              </div>
              
              <div className="mt-8">
                <h3 className="text-xl font-space-grotesk font-bold mb-4">Recent Tracks</h3>
                <div className="space-y-3">
                  {tracks.slice(0, 3).map((track) => (
                    <div key={track.id} className="flex items-center p-3 bg-white/5 rounded-lg">
                      {track.cover_url && (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? (
                        <video 
                          src={track.cover_url} 
                          alt={track.title}
                          className="w-12 h-12 rounded-lg object-cover mr-3"
                          autoPlay
                          loop
                          muted
                          playsInline
                        />
                      ) : (
                        <img 
                          src={track.cover_url} 
                          alt={track.title}
                          className="w-12 h-12 rounded-lg object-cover mr-3"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{track.title}</div>
                        <div className="text-sm text-gray-400 truncate">
                          {track.artist ? `by ${track.artist}` : ''}
                          {track.artist && track.roles && track.roles.length > 0 ? ' | ' : ''}
                          {track.roles && track.roles.length > 0 
                            ? track.roles.map(r => `${r.role}: ${r.name}`).join(', ')
                            : track.artist ? '' : 'No roles specified'}
                        </div>
                      </div>
                      <div className="text-sm text-gray-400">{track.duration}</div>
                    </div>
                  ))}
                  {tracks.length === 0 && (
                    <div className="text-gray-400 text-center py-4">
                      No tracks uploaded yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Birthday Management Tab */}
        {activeTab === 'birthdays' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Add Birthday Form */}
            <div className="glass-morphism-strong rounded-3xl p-8">
              <h2 className="text-2xl font-space-grotesk font-bold mb-6 flex items-center">
                <HiCake className="mr-2" />
                Add New Birthday
              </h2>
              {error && (
                <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}
              <form onSubmit={handleCreateBirthday}>
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Name</label>
                  <input
                    type="text"
                    value={birthdayName}
                    onChange={(e) => setBirthdayName(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Birthday</label>
                  <input
                    type="date"
                    value={birthdayDate}
                    onChange={(e) => setBirthdayDate(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Message</label>
                  <textarea
                    value={birthdayMessage}
                    onChange={(e) => setBirthdayMessage(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                    rows="3"
                    placeholder="Birthday message"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Avatar Emoji</label>
                    <input
                      type="text"
                      value={birthdayAvatar}
                      onChange={(e) => setBirthdayAvatar(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                      placeholder="🎂"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Favorite Color</label>
                    <input
                      type="text"
                      value={birthdayFavoriteColor}
                      onChange={(e) => setBirthdayFavoriteColor(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                      placeholder="#FF69B4"
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-gray-300 mb-2">Snapchat</label>
                  <input
                    type="text"
                    value={birthdaySnapchat}
                    onChange={(e) => setBirthdaySnapchat(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                    placeholder="snapchat_username"
                  />
                </div>
                
                <button
                  type="submit"
                  className="w-full btn-primary flex items-center justify-center"
                >
                  <HiCake className="mr-2" />
                  Add Birthday
                </button>
              </form>
            </div>

            {/* Birthday Stats */}
            <div className="glass-morphism-strong rounded-3xl p-8">
              <h2 className="text-2xl font-space-grotesk font-bold mb-6">Birthday Statistics</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/5 rounded-xl p-6">
                  <div className="text-3xl font-space-grotesk font-bold gradient-text-green mb-2">
                    {birthdays.length}
                  </div>
                  <div className="text-gray-300">Total Friends</div>
                </div>
                <div className="bg-white/5 rounded-xl p-6">
                  <div className="text-3xl font-space-grotesk font-bold gradient-text-green mb-2">
                    {birthdays.filter(b => {
                      const today = new Date();
                      const birthday = new Date(b.birthday);
                      return (
                        birthday.getMonth() === today.getMonth() &&
                        birthday.getDate() === today.getDate()
                      );
                    }).length}
                  </div>
                  <div className="text-gray-300">Today's Birthdays</div>
                </div>
              </div>
              
              <div className="mt-8">
                <h3 className="text-xl font-space-grotesk font-bold mb-4">Recent Birthdays</h3>
                <div className="space-y-3">
                  {birthdays.slice(0, 3).map((birthday) => {
                    const birthdayDate = new Date(birthday.birthday);
                    return (
                      <div key={birthday.id} className="flex items-center p-3 bg-white/5 rounded-lg">
                        <div className="text-2xl mr-3">{birthday.avatar || '🎂'}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{birthday.name}</div>
                          <div className="text-sm text-gray-400">
                            {birthdayDate.toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: birthday.favorite_color || '#FF69B4' }}
                        ></div>
                      </div>
                    );
                  })}
                  {birthdays.length === 0 && (
                    <div className="text-gray-400 text-center py-4">
                      No birthdays added yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Music Tracks List */}
        {activeTab === 'music' && (
          <div className="glass-morphism-strong rounded-3xl p-8">
            <h2 className="text-2xl font-space-grotesk font-bold mb-6 flex items-center">
              <HiMusicNote className="mr-2" />
              All Tracks
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 hidden md:table-row">
                    <th className="text-left py-3 px-4">Track</th>
                    <th className="text-left py-3 px-4">Artist</th>
                    <th className="text-left py-3 px-4">Roles</th>
                    <th className="text-left py-3 px-4">Album</th>
                    <th className="text-left py-3 px-4">Duration</th>
                    <th className="text-left py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((track) => (
                    <tr key={track.id} className="border-b border-white/5 hover:bg-white/5">
                      {editingTrackId === track.id ? (
                        // Edit mode
                        <td colSpan="6" className="py-6 px-4">
                          <div className="bg-white/5 rounded-xl p-6">
                            <h3 className="text-xl font-space-grotesk font-bold mb-4">Edit Track</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                              <div>
                                <label className="block text-gray-300 mb-2">Title</label>
                                <input
                                  type="text"
                                  value={editTitle}
                                  onChange={(e) => setEditTitle(e.target.value)}
                                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                />
                              </div>
                              <div>
                                <label className="block text-gray-300 mb-2">Artist</label>
                                <input
                                  type="text"
                                  value={editArtist}
                                  onChange={(e) => setEditArtist(e.target.value)}
                                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                />
                              </div>
                              <div>
                                <label className="block text-gray-300 mb-2">Album</label>
                                <input
                                  type="text"
                                  value={editAlbum}
                                  onChange={(e) => setEditAlbum(e.target.value)}
                                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                />
                              </div>
                              <div>
                                <label className="block text-gray-300 mb-2">Duration</label>
                                <input
                                  type="text"
                                  value={editDuration}
                                  onChange={(e) => setEditDuration(e.target.value)}
                                  placeholder="e.g., 3:45"
                                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                />
                              </div>
                              {/* Spotify URL */}
                              <div>
                                <label className="block text-gray-300 mb-2">Spotify Track URL</label>
                                <input
                                  type="text"
                                  value={editSpotifyUrl}
                                  onChange={(e) => setEditSpotifyUrl(e.target.value)}
                                  placeholder="https://open.spotify.com/track/..."
                                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                />
                              </div>
                              {/* YouTube URL */}
                              <div>
                                <label className="block text-gray-300 mb-2">YouTube Video URL</label>
                                <input
                                  type="text"
                                  value={editYoutubeUrl}
                                  onChange={(e) => setEditYoutubeUrl(e.target.value)}
                                  placeholder="https://www.youtube.com/watch?v=..."
                                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                />
                              </div>
                            </div>
                            
                            {/* Edit Roles Section */}
                            <div className="mb-6">
                              <div className="flex justify-between items-center mb-3">
                                <label className="block text-gray-300 font-medium">Roles & Contributors</label>
                                <button
                                  type="button"
                                  onClick={addEditRole}
                                  className="text-accent-green hover:text-accent-green/80 flex items-center text-sm"
                                >
                                  <HiPlus className="mr-1" /> Add Role
                                </button>
                              </div>
                              
                              <div className="space-y-3">
                                {editRoles.map((role, index) => (
                                  <div key={index} className="flex gap-3 items-center">
                                    <input
                                      type="text"
                                      placeholder="Role (e.g., Producer, Vocalist)"
                                      value={role.role}
                                      onChange={(e) => updateEditRole(index, 'role', e.target.value)}
                                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                    />
                                    <input
                                      type="text"
                                      placeholder="Name"
                                      value={role.name}
                                      onChange={(e) => updateEditRole(index, 'name', e.target.value)}
                                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                    />
                                    {editRoles.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => removeEditRole(index)}
                                        className="text-red-400 hover:text-red-300 p-2 rounded-full hover:bg-red-500/10"
                                      >
                                        <HiX />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            <div className="flex space-x-3">
                              <button
                                onClick={saveEditing}
                                className="btn-primary px-6 py-3"
                              >
                                Save Changes
                              </button>
                              <button
                                onClick={cancelEditing}
                                className="btn-secondary px-6 py-3"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      ) : (
                        // View mode - Mobile friendly layout
                        <td className="py-4 px-4 w-full">
                          <div className="md:hidden block mb-4">
                            <div className="flex items-center mb-3">
                              {track.cover_url && (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? (
                                <video 
                                  src={track.cover_url} 
                                  alt={track.title}
                                  className="w-16 h-16 rounded-lg object-cover mr-4"
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                />
                              ) : (
                                <img 
                                  src={track.cover_url} 
                                  alt={track.title}
                                  className="w-16 h-16 rounded-lg object-cover mr-4"
                                />
                              )}
                              <div>
                                <div className="font-medium text-lg">{track.title}</div>
                                <div className="text-gray-400 text-sm">{track.artist || 'Unknown Artist'}</div>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                              <div>
                                <div className="text-gray-400">Album</div>
                                <div>{track.album || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-gray-400">Duration</div>
                                <div>{track.duration}</div>
                              </div>
                            </div>
                            
                            <div className="mb-3">
                              <div className="text-gray-400 mb-1">Roles</div>
                              <div>
                                {track.roles && track.roles.length > 0 
                                  ? track.roles
                                      .filter(role => role.role && role.name)
                                      .slice(0, 3)
                                      .map((r, i) => (
                                        <span key={i} className="inline-block bg-white/10 rounded-full px-3 py-1 text-xs mr-1 mb-1">
                                          {r.role}: {r.name}
                                        </span>
                                      ))
                                  : 'No roles'}
                                {track.roles && track.roles.length > 3 && (
                                  <span className="text-xs text-gray-400">+{track.roles.length - 3} more</span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex space-x-2">
                              <button
                                onClick={() => startEditing(track)}
                                className="text-accent-green hover:text-accent-green/80 flex items-center text-sm px-3 py-2 rounded-lg hover:bg-accent-green/10 flex-1 justify-center"
                              >
                                <HiPencil className="mr-1" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(track.id)}
                                className="text-red-400 hover:text-red-300 flex items-center text-sm px-3 py-2 rounded-lg hover:bg-red-500/10 flex-1 justify-center"
                              >
                                <HiTrash className="mr-1" />
                                Delete
                              </button>
                            </div>
                          </div>
                          
                          {/* Desktop layout */}
                          <div className="hidden md:table-row">
                            <div className="flex items-center">
                              {track.cover_url && (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? (
                                <video 
                                  src={track.cover_url} 
                                  alt={track.title}
                                  className="w-12 h-12 rounded-lg object-cover mr-4"
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                />
                              ) : (
                                <img 
                                  src={track.cover_url} 
                                  alt={track.title}
                                  className="w-12 h-12 rounded-lg object-cover mr-4"
                                />
                              )}
                              <div>
                                <div className="font-medium">{track.title}</div>
                              </div>
                            </div>
                            <div className="py-4 px-4">
                              {track.artist || 'N/A'}
                            </div>
                            <div className="py-4 px-4">
                              {track.roles && track.roles.length > 0 
                                ? track.roles
                                    .filter(role => role.role && role.name)
                                    .slice(0, 2)
                                    .map((r, i) => (
                                      <span key={i} className="inline-block bg-white/10 rounded-full px-3 py-1 text-xs mr-1 mb-1">
                                        {r.role}: {r.name}
                                      </span>
                                    ))
                                : 'No roles'}
                              {track.roles && track.roles.length > 2 && (
                                <span className="text-xs text-gray-400">+{track.roles.length - 2} more</span>
                              )}
                            </div>
                            <div className="py-4 px-4">{track.album || 'N/A'}</div>
                            <div className="py-4 px-4">{track.duration}</div>
                            <div className="py-4 px-4">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => startEditing(track)}
                                  className="text-accent-green hover:text-accent-green/80 flex items-center text-sm px-3 py-1 rounded-lg hover:bg-accent-green/10"
                                >
                                  <HiPencil className="mr-1" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(track.id)}
                                  className="text-red-400 hover:text-red-300 flex items-center text-sm px-3 py-1 rounded-lg hover:bg-red-500/10"
                                >
                                  <HiTrash className="mr-1" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {tracks.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center py-12 text-gray-400">
                        <HiMusicNote className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                        <p className="text-lg">No tracks uploaded yet</p>
                        <p className="text-gray-500 mt-2">Upload your first track to get started</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Birthdays List */}
        {activeTab === 'birthdays' && (
          <div className="glass-morphism-strong rounded-3xl p-8">
            <h2 className="text-2xl font-space-grotesk font-bold mb-6 flex items-center">
              <HiCake className="mr-2" />
              All Birthdays
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 hidden md:table-row">
                    <th className="text-left py-3 px-4">Friend</th>
                    <th className="text-left py-3 px-4">Birthday</th>
                    <th className="text-left py-3 px-4">Message</th>
                    <th className="text-left py-3 px-4">Avatar</th>
                    <th className="text-left py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {birthdays.map((birthday) => {
                    const birthdayDate = new Date(birthday.birthday);
                    return (
                      <tr key={birthday.id} className="border-b border-white/5 hover:bg-white/5">
                        {editingBirthdayId === birthday.id ? (
                          // Edit mode
                          <td colSpan="5" className="py-6 px-4">
                            <div className="bg-white/5 rounded-xl p-6">
                              <h3 className="text-xl font-space-grotesk font-bold mb-4">Edit Birthday</h3>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                  <label className="block text-gray-300 mb-2">Name</label>
                                  <input
                                    type="text"
                                    value={editBirthdayName}
                                    onChange={(e) => setEditBirthdayName(e.target.value)}
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-gray-300 mb-2">Birthday</label>
                                  <input
                                    type="date"
                                    value={editBirthdayDate}
                                    onChange={(e) => setEditBirthdayDate(e.target.value)}
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                    required
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-gray-300 mb-2">Message</label>
                                  <textarea
                                    value={editBirthdayMessage}
                                    onChange={(e) => setEditBirthdayMessage(e.target.value)}
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                    rows="3"
                                  />
                                </div>
                                <div>
                                  <label className="block text-gray-300 mb-2">Avatar Emoji</label>
                                  <input
                                    type="text"
                                    value={editBirthdayAvatar}
                                    onChange={(e) => setEditBirthdayAvatar(e.target.value)}
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                    placeholder="🎂"
                                  />
                                </div>
                                <div>
                                  <label className="block text-gray-300 mb-2">Favorite Color</label>
                                  <input
                                    type="text"
                                    value={editBirthdayFavoriteColor}
                                    onChange={(e) => setEditBirthdayFavoriteColor(e.target.value)}
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                    placeholder="#FF69B4"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-gray-300 mb-2">Snapchat</label>
                                  <input
                                    type="text"
                                    value={editBirthdaySnapchat}
                                    onChange={(e) => setEditBirthdaySnapchat(e.target.value)}
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
                                    placeholder="snapchat_username"
                                  />
                                </div>
                              </div>
                              
                              <div className="flex space-x-3">
                                <button
                                  onClick={saveEditingBirthday}
                                  className="btn-primary px-6 py-3"
                                >
                                  Save Changes
                                </button>
                                <button
                                  onClick={cancelEditingBirthday}
                                  className="btn-secondary px-6 py-3"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        ) : (
                          // View mode - Mobile friendly layout
                          <td className="py-4 px-4 w-full">
                            <div className="md:hidden block mb-4">
                              <div className="flex items-center mb-3">
                                <div className="text-3xl mr-4">{birthday.avatar || '🎂'}</div>
                                <div>
                                  <div className="font-medium text-lg">{birthday.name}</div>
                                  <div className="text-gray-400 text-sm">
                                    {birthdayDate.toLocaleDateString('en-US', {
                                      month: 'long',
                                      day: 'numeric'
                                    })}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="mb-3">
                                <div className="text-gray-400 mb-1">Message</div>
                                <div className="text-sm">{birthday.message || 'No message'}</div>
                              </div>
                              
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => startEditingBirthday(birthday)}
                                  className="text-accent-green hover:text-accent-green/80 flex items-center text-sm px-3 py-2 rounded-lg hover:bg-accent-green/10 flex-1 justify-center"
                                >
                                  <HiPencil className="mr-1" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteBirthday(birthday.id)}
                                  className="text-red-400 hover:text-red-300 flex items-center text-sm px-3 py-2 rounded-lg hover:bg-red-500/10 flex-1 justify-center"
                                >
                                  <HiTrash className="mr-1" />
                                  Delete
                                </button>
                              </div>
                            </div>
                            
                            {/* Desktop layout */}
                            <div className="hidden md:table-row">
                              <div className="py-4 px-4">
                                <div className="font-medium">{birthday.name}</div>
                              </div>
                              <div className="py-4 px-4">
                                {birthdayDate.toLocaleDateString('en-US', {
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </div>
                              <div className="py-4 px-4 max-w-xs truncate">
                                {birthday.message || 'No message'}
                              </div>
                              <div className="py-4 px-4 text-2xl">
                                {birthday.avatar || '🎂'}
                              </div>
                              <div className="py-4 px-4">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => startEditingBirthday(birthday)}
                                    className="text-accent-green hover:text-accent-green/80 flex items-center text-sm px-3 py-1 rounded-lg hover:bg-accent-green/10"
                                  >
                                    <HiPencil className="mr-1" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteBirthday(birthday.id)}
                                    className="text-red-400 hover:text-red-300 flex items-center text-sm px-3 py-1 rounded-lg hover:bg-red-500/10"
                                  >
                                    <HiTrash className="mr-1" />
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {birthdays.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center py-12 text-gray-400">
                        <HiCake className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                        <p className="text-lg">No birthdays added yet</p>
                        <p className="text-gray-500 mt-2">Add your first birthday to get started</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;